import { LitElement, css, html, svg } from "lit";
import "./mercado-pago.js";
const STORAGE_KEY = "nearCredits.tokens.v1";

const DEFAULT_THEME = "auto";
const PENDING_CHECKOUT_KEY = "nearCredits.pendingCheckout";

const randomId = () =>
  (globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `token-${Math.random().toString(36).slice(2, 10)}`);

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const createToken = (overrides = {}, defaults = {}) => {
  const labelFallback =
    typeof defaults.labelFallback === "string" && defaults.labelFallback.trim()
      ? defaults.labelFallback.trim()
    : "Untitled payment account";
  const now = new Date().toISOString();
  const history = Array.isArray(overrides.history)
    ? overrides.history.filter((entry) => entry && typeof entry === "object")
    : [];
  const label =
    typeof overrides.label === "string" && overrides.label.trim() ? overrides.label.trim() : labelFallback;
  const tokenValue =
    typeof overrides.value === "string" && overrides.value.trim()
      ? overrides.value.trim()
      : (typeof overrides.id === "string" && overrides.id ? overrides.id : randomId());
  return {
    id: typeof overrides.id === "string" && overrides.id ? overrides.id : randomId(),
    label,
    value: tokenValue,
    balance: toNumber(overrides.balance, 0),
    currency: typeof overrides.currency === "string" && overrides.currency ? overrides.currency : "credits",
    status: overrides.status === "paused" ? "paused" : "active",
    createdAt: overrides.createdAt || now,
    updatedAt: overrides.updatedAt || now,
    email: overrides.email?.trim() || null,
    history
  };
};

const formatNumber = (value, currency, locale) => {
  if (!Number.isFinite(value)) return "0";
  if (currency && currency.toUpperCase() !== "CREDITS") {
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: currency.toUpperCase(),
      minimumFractionDigits: 2
    }).format(value);
  }
  return new Intl.NumberFormat(locale || undefined, { maximumFractionDigits: 2 }).format(value);
};

const clone = (value) => JSON.parse(JSON.stringify(value));

const sanitizeToken = (candidate, defaults) => {
  if (!candidate || typeof candidate !== "object") return null;
  return createToken(
    {
      ...candidate,
      history: Array.isArray(candidate.history) ? candidate.history : []
    },
    defaults
  );
};

const sanitizeState = (candidate, defaults) => {
  if (!candidate || typeof candidate !== "object") {
    return { tokens: [], activeTokenId: "" };
  }
  const tokens = Array.isArray(candidate.tokens) ? candidate.tokens : [];
  const normalizedTokens = tokens
    .map((token) => sanitizeToken(token, defaults))
    .filter(Boolean);

  const requestedActive =
    typeof candidate.activeTokenId === "string" && candidate.activeTokenId ? candidate.activeTokenId : "";
  const activeTokenId = normalizedTokens.some((token) => token.id === requestedActive)
    ? requestedActive
    : normalizedTokens[0]?.id ?? "";

  return { tokens: normalizedTokens, activeTokenId };
};

const FALLBACK_LANG = "en";

const TRANSLATIONS = {
  en: {
    defaults: { tokenLabel: "Untitled payment account", accountBase: "Account", accountShort: "A" },
    statuses: { active: "active", paused: "paused" },
    badge: {
      emptyTitle: "Buy credits",
      emptySubtitle: "No payment account",
      subtitle: (balance, status) => `${balance} · ${status}`
    },
    actions: {
      backup: "Backup",
      restore: "Restore",
      close: "Close",
      cancel: "Cancel",
      transfer: "Transfer",
      saveToken: "Save account",
      createToken: "Create account",
      importToken: "Import account",
      createPayment: "Create payment",
      creatingPayment: "Creating preference…",
      pauseToken: (label) => `Pause ${label}`,
      resumeToken: (label) => `Resume ${label}`,
      setActive: (label) => `Set ${label} as active`,
      tokenIsActive: (label) => `${label} is active`,
      transferFrom: (label) => `Transfer from ${label}`,
      buyCreditsFor: (label) => `Buy credits for ${label}`,
      copyToken: (label) => `Copy account key for ${label}`,
      shareToken: (label) => `Share ${label}`,
      deleteToken: (label) => `Delete ${label}`,
      openManualFab: "Create or import account",
      closeOverlay: "Close add account panel",
      createTokenOption: "Create new account…",
      checkoutFab: "Buy credits"
    },
    headings: {
      modalTitle: "Credits",
      transfer: "Transfer credits",
      addToken: "Add account manually",
      checkout: "Buy credits"
    },
    fields: {
      fromToken: "From account",
      toToken: "To account",
      transferAmount: "Credits to transfer",
      label: "Account name",
      tokenValue: "Account key",
      initialBalance: "Initial balance",
      checkoutAmount: (currency) => `Amount (${currency})`,
      selectToken: "Select account"
    },
    overlay: {
      createTitle: "Create payment account",
      importTitle: "Link existing account",
      createDescription:
        "Generate a new payment account on the server. You can optionally add a name and recovery email.",
      importDescription:
        "If you already have a payment account, paste its key here to sync it with this device.",
      emailLabel: "Recovery email (optional)",
      tokenPlaceholder: "Existing account key",
      mode: {
        create: "New account",
        import: "Import account"
      }
    },
    checkout: {
      forPrefix: "Checkout for:"
    },
    token: {
      balanceLabel: "Available",
      balanceUnits: { credits: "credits" },
      updated: (timestamp) => `Updated ${timestamp}`,
      empty:
        "Some features incur costs that must be covered by purchasing credits. You can create one or more payment accounts without providing personal data. Costs are deducted from your balance as you use the services. Accounts can be recharged, paused, resumed, transfer balance between them, or deleted whenever you need."
    },
    messages: {
      invalidDelete: "Only zero-balance accounts can be deleted.",
      cannotActivate: "Selected account is not available.",
      transferSelect: "Select accounts for transfer.",
      transferPositive: "Transfer amount must be positive.",
      transferBalance: "Not enough balance to transfer.",
      transferSuccess: "Credits transferred.",
      createTokenSuccess: (label) => (label ? `${label} created.` : "New account created."),
      createTokenError: "Failed to create account. Please try again.",
      importTokenRequired: "Please provide an account key.",
      importTokenNotFound: "Account not found.",
      importTokenSuccess: (label) => (label ? `${label} imported.` : "Account imported successfully."),
      importTokenError: "Failed to import account. Please verify the key and try again.",
      clipboardUnavailable: "Clipboard API unavailable.",
      copySuccess: "Account key copied to clipboard.",
      copyError: "Could not copy account key.",
      shareUnavailable: "Share API unavailable on this device.",
      shareError: "Unable to share account.",
      shareSuccess: "Account shared.",
      checkoutSelect: "Select an account for checkout.",
      checkoutAmount: "Enter a valid amount to purchase.",
      checkoutOpened: "Checkout started.",
      checkoutError: "Could not start checkout.",
      checkoutApproved: "Payment approved. Credits will refresh shortly.",
      checkoutPending: "Payment pending confirmation.",
      checkoutFailure: "Payment was cancelled or failed.",
      checkoutPreferenceReady: "Checkout preference generated. Continue the Mercado Pago flow manually.",
      restoreSuccess: "Backup restored.",
      restoreError: "Failed to restore accounts.",
      backupSuccess: "Backup exported.",
      tokensSanitized: "Stored credits data was sanitized before use."
    },
    footer: {
      persisted: "Accounts persist locally in this browser."
    },
    share: {
      title: "Near Credits Account",
      text: (label, value) => `${label ? `${label}: ` : ""}${value}`
    },
    sr: {
      addToken: "Create or import account",
      buyCredits: "Buy credits"
    }
  },
  es: {
    defaults: { tokenLabel: "Cuenta sin nombre", accountBase: "Cuenta", accountShort: "C" },
    statuses: { active: "activa", paused: "pausada" },
    badge: {
      emptyTitle: "Comprar créditos",
      emptySubtitle: "Sin cuenta activa",
      subtitle: (balance, status) => `${balance} · ${status}`
    },
    actions: {
      backup: "Hacer backup",
      restore: "Restaurar backup",
      close: "Cerrar",
      cancel: "Cancelar",
      transfer: "Transferir",
      saveToken: "Guardar cuenta",
      createToken: "Crear cuenta",
      importToken: "Importar cuenta",
      createPayment: "Crear pago",
      creatingPayment: "Creando preferencia…",
      pauseToken: (label) => `Pausar ${label}`,
      resumeToken: (label) => `Reanudar ${label}`,
      setActive: (label) => `Usar ${label} como activo`,
      tokenIsActive: (label) => `${label} activo`,
      transferFrom: (label) => `Transferir desde ${label}`,
      buyCreditsFor: (label) => `Comprar créditos para ${label}`,
      copyToken: (label) => `Copiar clave de ${label}`,
      shareToken: (label) => `Compartir ${label}`,
      deleteToken: (label) => `Eliminar ${label}`,
      openManualFab: "Crear o importar cuenta",
      closeOverlay: "Cerrar panel de alta manual",
      createTokenOption: "Crear nueva cuenta…",
      checkoutFab: "Comprar créditos"
    },
    headings: {
      modalTitle: "Créditos",
      transfer: "Transferir créditos",
      addToken: "Agregar cuenta manualmente",
      checkout: "Comprar créditos"
    },
    fields: {
      fromToken: "Cuenta origen",
      toToken: "Cuenta destino",
      transferAmount: "Créditos a transferir",
      label: "Nombre de la cuenta",
      tokenValue: "Clave de la cuenta",
      initialBalance: "Saldo inicial",
      checkoutAmount: (currency) => `Monto (${currency})`,
      selectToken: "Selecciona una cuenta"
    },
    overlay: {
      createTitle: "Crear cuenta de pago",
      importTitle: "Usar cuenta existente",
      createDescription:
        "Genera una nueva cuenta de pago en el servidor. Puedes agregar un nombre y un correo para recuperarla.",
      importDescription:
        "Si ya tienes una cuenta de pago, pega su clave aquí para sincronizarla con este dispositivo.",
      emailLabel: "Correo de recuperación (opcional)",
      tokenPlaceholder: "Clave de la cuenta existente",
      mode: {
        create: "Nueva cuenta",
        import: "Importar cuenta"
      }
    },
    checkout: {
      forPrefix: "Checkout para:"
    },
    token: {
      balanceLabel: "Disponibles",
      balanceUnits: { credits: "créditos" },
      updated: (timestamp) => `Actualizado ${timestamp}`,
      empty:
        "Algunas funciones generan costos que se cubren con créditos. Puedes crear una o varias cuentas de pago sin registrar tus datos personales. Los costos se descuentan del balance a medida que usas los servicios. Las cuentas pueden recargarse, pausarse, transferir saldo entre ellas o eliminarse cuando lo necesites."
    },
    messages: {
      invalidDelete: "Solo se pueden eliminar cuentas con saldo cero.",
      cannotActivate: "No es posible seleccionar la cuenta solicitada.",
      transferSelect: "Selecciona las cuentas para transferir.",
      transferPositive: "El monto a transferir debe ser positivo.",
      transferBalance: "No hay saldo suficiente para transferir.",
      transferSuccess: "Créditos transferidos.",
      createTokenSuccess: (label) => (label ? `${label} creada.` : "Cuenta creada."),
      createTokenError: "No se pudo crear la cuenta. Intenta nuevamente.",
      importTokenRequired: "Ingresa la clave de la cuenta.",
      importTokenNotFound: "No se encontró la cuenta.",
      importTokenSuccess: (label) => (label ? `${label} sincronizada.` : "Cuenta sincronizada."),
      importTokenError: "No se pudo importar la cuenta. Verifica la clave e intenta otra vez.",
      clipboardUnavailable: "La API del portapapeles no está disponible.",
      copySuccess: "Clave de la cuenta copiada al portapapeles.",
      copyError: "No se pudo copiar la clave de la cuenta.",
      shareUnavailable: "La API de compartir no está disponible en este dispositivo.",
      shareError: "No se pudo compartir la cuenta.",
      shareSuccess: "Cuenta compartida.",
      checkoutSelect: "Selecciona una cuenta para comprar créditos.",
      checkoutAmount: "Ingresa un monto válido para comprar.",
      checkoutOpened: "Checkout iniciado.",
      checkoutError: "No se pudo iniciar el checkout.",
      checkoutApproved: "Pago aprobado. Los créditos se actualizarán en instantes.",
      checkoutPending: "Pago pendiente de confirmación.",
      checkoutFailure: "El pago fue cancelado o falló.",
      checkoutPreferenceReady: "Preferencia generada. Continúa el flujo de Mercado Pago manualmente.",
      restoreSuccess: "Respaldo restaurado.",
      restoreError: "No se pudo restaurar el respaldo.",
      backupSuccess: "Respaldo exportado.",
      tokensSanitized: "Los datos de créditos guardados fueron saneados antes de usarlos."
    },
    footer: {
      persisted: "Las cuentas se guardan localmente en este navegador."
    },
    share: {
      title: "Cuenta de Near Credits",
      text: (label, value) => `${label ? `${label}: ` : ""}${value}`
    },
    sr: {
      addToken: "Crear o importar cuenta",
      buyCredits: "Comprar créditos"
    }
  }
};

const SUPPORTED_LANGS = Object.keys(TRANSLATIONS);

const normalizeLang = (value) => (typeof value === "string" && value ? value.toLowerCase().split("-")[0] : "");

const detectLanguage = () => {
  const browserLang = normalizeLang(typeof navigator !== "undefined" ? navigator.language : "");
  return SUPPORTED_LANGS.includes(browserLang) ? browserLang : FALLBACK_LANG;
};

const getNestedValue = (source, pathParts) =>
  pathParts.reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), source);

const translate = (lang, key, ...args) => {
  const path = key.split(".");
  const dictionary = TRANSLATIONS[lang] || TRANSLATIONS[FALLBACK_LANG];
  let value = getNestedValue(dictionary, path);
  if (value === undefined && lang !== FALLBACK_LANG) {
    value = getNestedValue(TRANSLATIONS[FALLBACK_LANG], path);
  }
  if (typeof value === "function") {
    return value(...args);
  }
  return value !== undefined ? value : key;
};

const createIcon = (inner) =>
  svg`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;

const ICONS = {
  pause: createIcon(
    svg`<rect x="6" y="4" width="4" height="16" rx="2"></rect><rect x="14" y="4" width="4" height="16" rx="2"></rect>`
  ),
  play: createIcon(svg`<polygon points="7 4 17 12 7 20 7 4"></polygon>`),
  check: createIcon(svg`<path d="M20 6 9 17l-5-5"></path>`),
  dot: createIcon(svg`<circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="3"></circle>`),
  transfer: createIcon(
    svg`<polyline points="17 11 21 7 17 3"></polyline><line x1="21" y1="7" x2="9" y2="7"></line><polyline points="7 13 3 17 7 21"></polyline><line x1="15" y1="17" x2="3" y2="17"></line>`
  ),
  cart: createIcon(
    svg`<circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>`
  ),
  copy: createIcon(
    svg`<rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>`
  ),
  share: createIcon(
    svg`<circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><path d="M8.59 13.51 15.42 17.49"></path><path d="M15.41 6.51 8.59 10.49"></path>`
  ),
  trash: createIcon(
    svg`<path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path><path d="M10 11v6"></path><path d="M14 11v6"></path><path d="M5 6V4a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2"></path>`
  ),
  plus: createIcon(svg`<line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>`),
  x: createIcon(svg`<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>`)
};


class NearCredits extends LitElement {
  static properties = {
    showModal: { state: true },
    tokens: { state: true },
    activeTokenId: { type: String, attribute: "active-token" },
    busy: { state: true },
    message: { state: true },
    messageType: { state: true },
    apiBase: { type: String, attribute: "api-base" },
    currency: { type: String },
    theme: { type: String, reflect: true },
    lang: { type: String, reflect: true },
    transferFromId: { state: true },
    transferTargetId: { state: true },
    transferAmount: { state: true },
    addTokenMode: { state: true },
    draftLabel: { state: true },
    createEmail: { state: true },
    importTokenId: { state: true },
    importLabel: { state: true },
    checkoutTokenId: { state: true },
    checkoutAmount: { state: true },
    addTokenVisible: { state: true },
    mpPublicKey: { type: String, attribute: "mp-public-key" },
    checkoutPreferenceId: { state: true },
    checkoutInitPoint: { state: true }
  };

  static styles = [
    css`
      :host {
        display: inline-flex;
        position: relative;
        --nc-background: var(--near-credits-background, color-mix(in srgb, var(--nc-accent) 12%, rgba(15, 23, 42, 0.88)));
        --nc-background-hover: var(--near-credits-background-hover, color-mix(in srgb, var(--nc-accent) 18%, rgba(15, 23, 42, 0.95)));
        --nc-color: var(--near-credits-color, var(--pico-color, #e5e7eb));
        --nc-accent: var(--near-credits-accent, var(--pico-primary, #0ea5e9));
        --nc-accent-strong: var(--near-credits-accent-strong, var(--pico-primary-hover, color-mix(in srgb, var(--nc-accent) 82%, #ffffff 18%)));
        --nc-border-radius: var(--near-credits-border-radius, 999px);
        --nc-font-heading: var(--near-credits-font-heading, "Space Grotesk", var(--pico-font-family, "Inter", system-ui, sans-serif));
        --nc-font-family: var(--near-credits-font-family, var(--pico-font-family, "Inter", "Manrope", system-ui, sans-serif));
        --nc-modal-backdrop: var(--near-credits-modal-backdrop, rgba(15, 23, 42, 0.75));
        --nc-card-background: var(--near-credits-card-background, color-mix(in srgb, var(--pico-muted-border-color, #94a3b8) 12%, #0f172a 88%));
        --nc-card-overlay: var(--near-credits-card-overlay, linear-gradient(145deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02)));
        --nc-card-border: var(--near-credits-card-border, var(--pico-muted-border-color, rgba(148, 163, 184, 0.25)));
        --nc-muted: var(--near-credits-muted, var(--pico-muted-color, #94a3b8));
        --nc-light-text: var(--near-credits-light-text, var(--pico-color, #0f172a));
        --nc-light-bg: var(--near-credits-light-bg, var(--pico-background-color, #f9fafb));
        --nc-light-border: var(--near-credits-light-border, var(--pico-muted-border-color, rgba(15, 23, 42, 0.12)));
        --nc-danger: var(--near-credits-danger, var(--pico-color-red, #ef4444));
        --nc-success: var(--near-credits-success, var(--pico-color-green, #10b981));
        --nc-shadow: 0 20px 50px rgba(15, 23, 42, 0.4);
        font-family: var(--nc-font-family);
        position: relative;
        min-width: 100px;
        min-height: 40px;
        z-index: 1000;
        font-size: 15px;
      }

      :host([theme="light"]) {
        --nc-background: rgba(255, 255, 255, 0.86);
        --nc-background-hover: rgba(255, 255, 255, 0.98);
        --nc-color: #0f172a;
        --nc-card-background: #f9fafb;
        --nc-card-border: rgba(15, 23, 42, 0.12);
        --nc-muted: #475569;
        --nc-card-overlay: linear-gradient(145deg, rgba(15, 23, 42, 0.02), rgba(15, 23, 42, 0.06));
      }

      :host([theme="dark"]) {
        --nc-background: rgba(17, 24, 39, 0.75);
        --nc-background-hover: rgba(17, 24, 39, 0.91);
        --nc-color: #f9fafb;
        --nc-shadow: 0 24px 60px rgba(0, 0, 0, 0.55);
      }

      .nc-trigger {
        display: inline-flex;
        align-items: center;
        gap: 0.65rem;
        padding: 0.4rem 0.85rem;
        background: var(--nc-background);
        color: var(--nc-color);
        border-radius: var(--nc-border-radius);
        border: 1px solid var(--nc-card-border);
        cursor: pointer;
        transition: background 160ms ease, transform 160ms ease, border 160ms ease, box-shadow 160ms ease;
        font: 600 0.95rem/1.3 var(--nc-font-family);
        min-width: 150px;
        justify-content: space-between;
        box-shadow: 0 14px 30px rgba(15, 23, 42, 0.35);
        backdrop-filter: blur(4px);
      }

      .nc-trigger:hover,
      .nc-trigger:focus-visible {
        background: color-mix(in srgb, var(--nc-background-hover) 90%, var(--nc-accent) 10%);
        transform: translateY(-1px);
        border-color: color-mix(in srgb, var(--nc-accent) 45%, transparent);
        box-shadow: 0 18px 40px color-mix(in srgb, var(--nc-accent) 22%, transparent);
        outline: 2px solid color-mix(in srgb, var(--nc-accent) 35%, transparent);
        outline-offset: 2px;
      }

      .avatar {
        width: 2rem;
        height: 2rem;
        border-radius: 999px;
        background: rgba(128, 128, 128, 0.16);
        display: grid;
        place-items: center;
        font-weight: 600;
        font-size: 1.6rem;
        padding: 2px;
      }

      .summary {
        display: flex;
        flex-direction: column;
      }

      .summary small {
        color: var(--nc-muted);
        font-size: 0.7rem;
      }

      .trigger-left {
        display: inline-flex;
        gap: 0.75rem;
        align-items: center;
      }

      .badge-pill {
        border-radius: 999px;
        padding: 0.2rem 0.75rem;
        font-size: 0.8rem;
        font-weight: 600;
        color: var(--nc-light-text);
        background: color-mix(in srgb, var(--nc-accent) 40%, transparent);
        border: 1px solid color-mix(in srgb, var(--nc-accent-strong) 40%, transparent);
      }

      .badge-pill.info {
        background: color-mix(in srgb, var(--nc-accent) 35%, transparent);
      }

      .badge-pill.success {
        background: color-mix(in srgb, var(--nc-success) 35%, transparent);
        border-color: color-mix(in srgb, var(--nc-success) 40%, transparent);
      }

      .badge-pill.error {
        background: color-mix(in srgb, var(--nc-danger) 35%, transparent);
        border-color: color-mix(in srgb, var(--nc-danger) 40%, transparent);
      }

      dialog::backdrop {
        background: var(--nc-modal-backdrop);
        backdrop-filter: blur(8px);
      }

      dialog {
        border: none;
        border-radius: 18px;
        padding: 0;
        max-width: min(720px, 90vw);
        width: 100%;
        background: var(--nc-card-background);
        color: var(--nc-color);
        border: 1px solid var(--nc-card-border);
        box-shadow: var(--nc-shadow);
        overflow: hidden;
        position: relative;
        backdrop-filter: blur(10px);
        background-image: var(--nc-card-overlay);
      }

      dialog[popover] {
        position: fixed;
      }

      .modal-head {
        padding: 1.25rem 1.5rem 1rem;
        border-bottom: 1px solid var(--nc-card-border);
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
      }

      .modal-head h2 {
        margin: 0;
        font-size: 1.2rem;
      }

      .modal-body {
        position: relative;
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 1.5rem;
        padding: 1.5rem;
        background: inherit;
      }

      .modal-body.checkout-active {
        grid-template-columns: minmax(0, 1fr);
      }

      .modal-body.checkout-active .tokens {
        display: none;
      }

      .tokens {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        max-height: none;
        overflow: visible;
      }

      .token-card {
        padding: 1.1rem 1.35rem;
        border-radius: 16px;
        border: 1px solid var(--nc-card-border);
        background: radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.04), transparent 42%),
          color-mix(in srgb, var(--nc-card-background) 90%, transparent);
        display: grid;
        gap: 0.85rem;
        box-shadow: 0 18px 36px rgba(0, 0, 0, 0.25);
        font-variant-numeric: tabular-nums;
      }

      .token-card.token-empty {
        text-align: center;
        gap: 1rem;
      }

      .token-card.token-empty p {
        margin: 0;
        font-size: 0.95rem;
        color: var(--nc-muted);
        line-height: 1.6;
      }

      .token-card.token-empty .nc-btn {
        margin: 0 auto;
      }

      .token-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .token-header--select {
        gap: 0.75rem;
      }

      .token-label {
        font-family: var(--nc-font-heading);
        font-weight: 700;
        letter-spacing: -0.01em;
      }

      .token-meta {
        font-size: 0.8rem;
        color: var(--nc-muted);
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
      }

      .token-details {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;
        min-width: 0;
        flex: 1 1 auto;
      }

      .token-select {
        display: grid;
        gap: 0.35rem;
        min-width: 200px;
        flex: 1 0 220px;
      }

      .token-select-label {
        font-size: 0.75rem;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--nc-muted);
      }

      .token-select select {
        border-radius: 12px;
        border: 1px solid var(--nc-card-border);
        padding: 0.55rem 0.8rem;
        background: color-mix(in srgb, var(--nc-card-background) 92%, transparent);
        color: inherit;
        font: 700 1.2rem/1.3 var(--nc-font-heading);
        width: 100%;
        outline: none;
        transition: border 140ms ease, box-shadow 140ms ease, background 140ms ease;
      }

      .token-select select:focus-visible {
        border-color: var(--nc-accent);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--nc-accent) 35%, transparent);
      }

      .token-status {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 0.4rem;
        min-width: 160px;
        text-align: right;
      }

      .token-meta-value {
        font-size: 0.8rem;
        color: var(--nc-muted);
        word-break: break-all;
      }

      .token-summary {
        align-items: flex-end;
        flex: 0 0 auto;
      }

      .token-date {
        font-size: 0.75rem;
      }

      .token-balance {
        display: flex;
        flex-direction: column;
        margin-top: 0.4rem;
        text-align: right;
      }

      .token-balance-amount {
        font-size: 2.15rem;
        font-weight: 800;
        line-height: 1.05;
        color: var(--nc-accent);
        font-family: var(--nc-font-heading);
      }

      .token-balance-label {
        font-size: 0.75rem;
        color: var(--nc-muted);
        text-transform: uppercase;
        letter-spacing: 0.08em;
      }

      .token-email {
        font-size: 0.8rem;
        color: var(--nc-muted);
        word-break: break-word;
      }

      .chip {
        border-radius: 999px;
        border: 1px solid var(--nc-card-border);
        padding: 0.1rem 0.55rem;
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }

      .chip.paused {
        background: rgba(255, 255, 255, 0.05);
        color: var(--nc-muted);
      }

      .token-line{
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .token-actions {
        display: flex;
        flex-wrap: wrap;
        gap: .75rem;
        justify-content: center;
      }

      button.nc-btn {
        border-radius: 10px;
        border: 1px solid var(--nc-card-border);
        padding: 0.5rem 0.9rem;
        background: rgba(255, 255, 255, 0.04);
        color: inherit;
        font: inherit;
        cursor: pointer;
        transition: background 140ms ease, transform 140ms ease, border 140ms ease, box-shadow 140ms ease;
        min-width: 2.25rem;
        min-height: 2.25rem;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      button.nc-btn:hover,
      button.nc-btn:focus-visible {
        background: color-mix(in srgb, var(--nc-accent) 14%, transparent);
        border-color: color-mix(in srgb, var(--nc-accent) 40%, transparent);
        transform: translateY(-1px);
        box-shadow: 0 12px 28px color-mix(in srgb, var(--nc-accent) 22%, transparent);
        outline: 2px solid color-mix(in srgb, var(--nc-accent) 35%, transparent);
        outline-offset: 2px;
      }

      button.nc-btn.primary {
        background: linear-gradient(135deg, var(--nc-accent), var(--nc-accent-strong));
        border-color: color-mix(in srgb, var(--nc-accent-strong) 65%, transparent);
        color: #fff;
        box-shadow: 0 14px 32px color-mix(in srgb, var(--nc-accent) 30%, transparent);
      }

      button.nc-btn.danger {
        border-color: rgba(220, 38, 38, 0.35);
        color: var(--nc-danger);
        background: rgba(220, 38, 38, 0.08);
      }

      button.nc-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
        box-shadow: none;
        outline: none;
      }

      button.nc-btn svg {
        width: 1.1rem;
        height: 1.1rem;
      }

      .section {
        display: grid;
        gap: 0.75rem;
      }

      .section h3 {
        margin: 0;
        font-size: 1.05rem;
        font-family: var(--nc-font-heading);
        letter-spacing: -0.01em;
      }

      label.field {
        display: grid;
        gap: 0.35rem;
        font-size: 0.85rem;
        color: var(--nc-muted);
      }

      label.field .field-hint {
        font-size: 0.75rem;
        color: var(--nc-muted);
      }

      label.field input,
      label.field select {
        border-radius: 12px;
        border: 1px solid var(--nc-card-border);
        padding: 0.55rem 0.85rem;
        background: color-mix(in srgb, var(--nc-card-background) 92%, transparent);
        color: inherit;
        font: inherit;
        transition: border 140ms ease, box-shadow 140ms ease, background 140ms ease;
      }

      label.field input:focus-visible,
      label.field select:focus-visible {
        outline: none;
        border-color: color-mix(in srgb, var(--nc-accent) 45%, transparent);
        box-shadow: 0 0 0 3px color-mix(in srgb, var(--nc-accent) 32%, transparent);
      }

      .message {
        border-radius: 10px;
        padding: 0.75rem 1rem;
        font-size: 0.85rem;
        background: color-mix(in srgb, var(--nc-accent) 14%, transparent);
        border: 1px solid color-mix(in srgb, var(--nc-accent) 35%, transparent);
        color: inherit;
      }

      .message.error {
        background: rgba(220, 38, 38, 0.12);
        border-color: rgba(220, 38, 38, 0.3);
        color: var(--nc-danger);
      }

      .message.success {
        background: rgba(16, 185, 129, 0.12);
        border-color: rgba(16, 185, 129, 0.3);
        color: var(--nc-success);
      }

      .checkout-summary {
        margin-top: 1rem;
        padding: 0.9rem 1rem;
        border-radius: 12px;
        border: 1px solid var(--nc-card-border);
        background: color-mix(in srgb, var(--nc-card-background) 90%, transparent);
        display: grid;
        gap: 0.75rem;
        font-size: 0.85rem;
      }

      .checkout-summary-body {
        display: grid;
        gap: 0.5rem;
      }

      .checkout-summary code {
        display: block;
        padding: 0.35rem 0.55rem;
        border-radius: 8px;
        background: rgba(255, 255, 255, 0.08);
        font-family: var(--nc-font-family);
        font-size: 0.8rem;
        word-break: break-all;
      }

      .checkout-summary .summary-label {
        display: block;
        font-size: 0.75rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--nc-muted);
        margin-bottom: 0.25rem;
      }

      .checkout-form {
        display: grid;
        gap: 1rem;
      }

      .amount-options {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
        gap: 0.75rem;
      }

      .amount-options button {
        border-radius: 12px;
        border: 1px solid var(--nc-card-border);
        padding: 0.75rem 0.5rem;
        background: rgba(255, 255, 255, 0.06);
        color: inherit;
        font: inherit;
        cursor: pointer;
        transition: background 140ms ease, border 140ms ease, transform 140ms ease;
      }

      .amount-options button:hover,
      .amount-options button:focus-visible {
        background: color-mix(in srgb, var(--nc-accent) 18%, transparent);
        border-color: color-mix(in srgb, var(--nc-accent) 40%, transparent);
      }

      .amount-options button.active {
        background: color-mix(in srgb, var(--nc-accent) 22%, transparent);
        border-color: color-mix(in srgb, var(--nc-accent) 60%, transparent);
      }

      .modal-footer {
        padding: 1rem 1rem 1rem 1rem;
        border-top: 1px solid var(--nc-card-border);
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 0.75rem;
      }

      .add-token-overlay {
        position: absolute;
        inset: 0;
        background: color-mix(in srgb, var(--nc-card-background) 72%, transparent);
        display: grid;
        place-items: center;
        padding: 1.5rem;
        z-index: 10;
        backdrop-filter: blur(6px);
      }

      .add-token-card {
        width: min(480px, 100%);
        background: var(--nc-card-background);
        border: 1px solid var(--nc-card-border);
        border-radius: 18px;
        padding: 1.5rem;
        display: grid;
        gap: 1.25rem;
        max-height: min(90vh, 560px);
        overflow-y: auto;
      }

      .add-token-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
      }

      .add-token-head h3 {
        margin: 0;
        font-size: 1.15rem;
      }

      .add-token-form {
        display: grid;
        gap: 0.9rem;
      }

      .add-token-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.75rem;
      }

      .skeleton {
        background: linear-gradient(90deg, rgba(255, 255, 255, 0.05) 25%, rgba(255, 255, 255, 0.12) 37%, rgba(255, 255, 255, 0.05) 63%);
        background-size: 400% 100%;
        animation: shimmer 1400ms ease infinite;
        border-radius: 8px;
      }

      @keyframes shimmer {
        0% {
          background-position: 100% 0;
        }
        100% {
          background-position: -100% 0;
        }
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .footer-actions {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
      }

      .nc-btn.secondary {
        background: rgba(255, 255, 255, 0.08);
        color: inherit;
        padding: 0.35rem 0.7rem;
        font-size: 0.82rem;
      }

      .nc-btn.secondary:hover,
      .nc-btn.secondary:focus-visible {
        background: rgba(255, 255, 255, 0.15);
      }

      .mode-switch {
        display: inline-flex;
        border-radius: 999px;
        border: 1px solid var(--nc-card-border);
        padding: 0.2rem;
        gap: 0.15rem;
        background: color-mix(in srgb, var(--nc-card-background) 96%, transparent);
      }

      .mode-switch button {
        border: none;
        background: transparent;
        color: inherit;
        padding: 0.35rem 0.9rem;
        border-radius: 999px;
        font: inherit;
        cursor: pointer;
        transition: background 120ms ease, color 120ms ease;
      }

      .mode-switch button.active {
        background: var(--nc-accent);
        color: #fff;
      }
      .nc-cta {
        max-width:250px;
        margin: auto;
        display: inline-flex;
        align-items: center;
        gap: 0.65rem;
        padding: 0.75rem 1.5rem;
        border: none;
        border-radius: 3rem;
        background: var(--nc-accent);
        color: #fff;
        font: inherit;
        font-weight: 600;
        letter-spacing: 0.01em;
        box-shadow: 0 10px 24px color-mix(in srgb, var(--nc-accent) 35%, transparent);
        cursor: pointer;
        transition: transform 160ms ease, box-shadow 160ms ease, background 160ms ease;
      }

      .nc-cta:hover,
      .nc-cta:focus-visible {
        transform: translateY(-2px);
        box-shadow: 0 14px 32px color-mix(in srgb, var(--nc-accent) 45%, transparent);
        background: color-mix(in srgb, var(--nc-accent) 92%, #ffffff 8%);
      }

      .nc-cta:disabled {
        display:none;
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
        box-shadow: 0 8px 20px color-mix(in srgb, var(--nc-accent) 25%, transparent);
      }

      .nc-cta svg {
        width: 1.1rem;
        height: 1.1rem;
      }
  
    `
  ];

  constructor() {
    super();
    this._pendingRefreshTimers = new Map();
    this.badgeMessage = "";
    this.badgeMessageType = "info";
    this.badgeMessageTimer = null;
    this._nearApiListener = () => {
      this.#syncTokensFromNearApi();
    };
    this._windowNearApiListener = () => {
      this.#syncTokensFromNearApi();
    };
    const initialLang = this.#resolveLanguage(this.getAttribute("lang")) ?? this.#detectLanguage();
    this.lang = initialLang;
    this.showModal = false;
    this.tokens = [];
    this.activeTokenId = "";
    this.busy = false;
    this.message = "";
    this.messageType = "info";
    this.apiBase = "/api";
    this.currency = "USD";
    this.theme = DEFAULT_THEME;
    this.transferFromId = "";
    this.transferTargetId = "";
    this.transferAmount = "";
    this.addTokenMode = "create";
    this.draftLabel = "";
    this.createEmail = "";
    this.importTokenId = "";
    this.importLabel = "";
    this.checkoutTokenId = "";
    this.checkoutAmount = "";
    this.messageTimer = null;
    this.addTokenVisible = false;
    this._syncingTokens = new Set();
    this._suspendDialogCleanup = false;
    this.mpPublicKey = this.getAttribute("mp-public-key") || "";
    this.checkoutPreferenceId = "";
    this.checkoutInitPoint = "";
  }

  #resolveLanguage(value) {
    const normalized = normalizeLang(value);
    return SUPPORTED_LANGS.includes(normalized) ? normalized : undefined;
  }

  #detectLanguage() {
    return detectLanguage();
  }

  t(key, ...args) {
    const lang = this.#resolveLanguage(this.lang) ?? FALLBACK_LANG;
    return translate(lang, key, ...args);
  }

  get #locale() {
    return this.#resolveLanguage(this.lang) ?? FALLBACK_LANG;
  }

  get #tokenDefaults() {
    return { labelFallback: this.t("defaults.tokenLabel") };
  }

  #syncTokenSelect() {
    const select = this.renderRoot?.querySelector(".token-select select");
    if (!select) return;
    const activeId = this.activeTokenId || this.activeToken?.id || "";
    if (activeId && select.value !== activeId) {
      select.value = activeId;
    }
  }

  #existingLabelSet(excludeId) {
    return new Set(
      this.tokens
        .filter((token) => (excludeId ? token.id !== excludeId : true))
        .map((token) =>
          ((token?.label ?? "").toString().trim().toLowerCase())
        )
        .filter((value) => value)
    );
  }

  #generateDefaultLabel({ excludeId } = {}) {
    const base = this.t("defaults.accountBase") || "Account";
    const existing = this.#existingLabelSet(excludeId);
    let index = 1;
    let candidate = `${base} ${index}`;
    while (existing.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${base} ${index}`;
    }
    return candidate;
  }

  #ensureUniqueLabel(label, { excludeId } = {}) {
    const trimmed = typeof label === "string" ? label.trim() : "";
    const existing = this.#existingLabelSet(excludeId);
    if (!trimmed) {
      return this.#generateDefaultLabel({ excludeId });
    }
    const lower = trimmed.toLowerCase();
    if (!existing.has(lower)) {
      return trimmed;
    }
    const base = this.t("defaults.accountBase") || "Account";
    const pattern = new RegExp(`^${base}\\s+(\\d+)$`, "i");
    if (pattern.test(trimmed)) {
      return this.#generateDefaultLabel({ excludeId });
    }
    let index = 2;
    let candidate = `${trimmed} (${index})`;
    while (existing.has(candidate.toLowerCase())) {
      index += 1;
      candidate = `${trimmed} (${index})`;
    }
    return candidate;
  }

  #clearPendingRefreshTimer(key) {
    if (!this._pendingRefreshTimers?.size) return;
    const timerId = this._pendingRefreshTimers.get(key);
    if (timerId) {
      clearInterval(timerId);
    }
    this._pendingRefreshTimers.delete(key);
  }

  #clearAllPendingRefreshTimers() {
    if (!this._pendingRefreshTimers?.size) return;
    Array.from(this._pendingRefreshTimers.keys()).forEach((key) => this.#clearPendingRefreshTimer(key));
  }

  #recordPendingCheckout({ tokenId, paymentId } = {}) {
    if (!tokenId || typeof sessionStorage === "undefined") return;
    try {
      const payload = {
        tokenId,
        paymentId: paymentId || null,
        recordedAt: Date.now()
      };
      sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(payload));
    } catch (error) {
      console.warn("[near-credits] unable to persist pending checkout marker", error);
    }
  }

  #consumePendingCheckout() {
    if (typeof sessionStorage === "undefined") return null;
    try {
      const raw = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
      sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.tokenId === "string" && parsed.tokenId.trim()) {
        return {
          tokenId: parsed.tokenId.trim(),
          paymentId: typeof parsed.paymentId === "string" && parsed.paymentId.trim() ? parsed.paymentId.trim() : null
        };
      }
    } catch (error) {
      console.warn("[near-credits] unable to read pending checkout marker", error);
    }
    return null;
  }

  #schedulePostCheckoutRefresh(tokenId, { attempts = 5, intervalMs = 3500 } = {}) {
    if (!tokenId || !this.#canSyncBackend()) return;
    if (this._pendingRefreshTimers.has(tokenId)) return;
    this.#setBadgeMessage(this.t("messages.checkoutPending"), "info", 0);
    let remaining = attempts;
    const tick = async () => {
      const token = this.tokens.find((t) => t.id === tokenId);
      if (!token) {
        this.#clearPendingRefreshTimer(tokenId);
        return;
      }
      await this.#refreshTokenFromBackend(token);
      remaining -= 1;
      if (remaining <= 0) {
        this.#clearPendingRefreshTimer(tokenId);
        this.#setBadgeMessage("", "info", 0);
      }
    };
    tick();
    const timerId = setInterval(tick, intervalMs);
    this._pendingRefreshTimers.set(tokenId, timerId);
  }

  async #fetchPaymentStatus(paymentId) {
    if (!paymentId || !this.#canSyncBackend()) return null;
    try {
      const response = await fetch(`${this.#apiBaseUrl}/payments/${encodeURIComponent(paymentId)}/status`, {
        headers: { Accept: "application/json" }
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data;
    } catch (error) {
      console.warn("[near-credits] payment status fetch failed", error);
      return null;
    }
  }

  #schedulePaymentStatusWatch({ paymentId, tokenId, attempts = 20, intervalMs = 4000 } = {}) {
    if (!paymentId || this._pendingRefreshTimers.has(`pay:${paymentId}`)) return;
    this.#setBadgeMessage(this.t("messages.checkoutPending"), "info", 0);
    let remaining = attempts;
    const tick = async () => {
      const result = await this.#fetchPaymentStatus(paymentId);
      const status = (result?.status || "").toLowerCase();
      if (status && status !== "pending" && status !== "in_process" && status !== "in_mediation") {
        this.#clearPendingRefreshTimer(`pay:${paymentId}`);
        this.#consumePendingCheckout();
        if (status === "approved") {
          this.#setBadgeMessage(this.t("messages.checkoutApproved"), "success", 5000);
          if (tokenId) {
            const token = this.tokens.find((t) => t.id === tokenId);
            if (token) {
              this.#refreshTokenFromBackend(token);
            } else {
              this.#refreshAllTokensFromBackend();
            }
          } else {
            this.#refreshAllTokensFromBackend();
          }
        } else {
          this.#setMessage(this.t("messages.checkoutFailure"), "error");
          this.#setBadgeMessage(this.t("messages.checkoutFailure"), "error", 6000);
        }
        return;
      }
      remaining -= 1;
      if (remaining <= 0) {
        this.#clearPendingRefreshTimer(`pay:${paymentId}`);
        this.#setBadgeMessage(this.t("messages.checkoutFailure"), "error", 6000);
      }
    };
    tick();
    const timerId = setInterval(tick, intervalMs);
    this._pendingRefreshTimers.set(`pay:${paymentId}`, timerId);
  }

  #resumePendingCheckoutRefresh() {
    const pending = this.#consumePendingCheckout();
    if (!pending) return;
    const { tokenId, paymentId } = pending;
    if (paymentId) {
      this.#schedulePaymentStatusWatch({ paymentId, tokenId });
      return;
    }
    if (tokenId) {
      if (this.tokens.some((token) => token.id === tokenId)) {
        this.#schedulePostCheckoutRefresh(tokenId);
      } else {
        this.#refreshAllTokensFromBackend();
        this.updateComplete.then(() => this.#schedulePostCheckoutRefresh(tokenId));
      }
    }
  }

  #initialsForToken(token) {
    if (!token) return "NC";
    const label = (token.label || "").trim();
    const base = this.t("defaults.accountBase") || "Account";
    const short = ((this.t("defaults.accountShort") || base[0] || "A").toString().slice(0, 1) || "A").toUpperCase();
    if (!label) {
      return short;
    }
    const pattern = new RegExp(`^${base}\\s+(\\d+)$`, "i");
    const match = pattern.exec(label);
    if (match) {
      return `${short}${match[1]}`;
    }
    const words = label.split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return short;
    }
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }

  #formatDate(value) {
    try {
      return new Date(value).toLocaleString(this.#locale);
    } catch {
      return value;
    }
  }

  #currencyUnits(token) {
    const unit = (token.currency || this.currency || "").toLowerCase();
    if (!unit) return "";
    if (unit === "credits") return this.t("token.balanceUnits.credits");
    return unit.toUpperCase();
  }

  #canSyncBackend() {
    return typeof fetch === "function" && typeof this.apiBase === "string" && this.apiBase.trim();
  }

  get #apiBaseUrl() {
    const base = this.apiBase && typeof this.apiBase === "string" ? this.apiBase.trim() : "";
    if (!base) return "/api";
    return base.endsWith("/") ? base.slice(0, -1) : base;
  }

  #resetCheckoutSummary() {
    this.checkoutPreferenceId = "";
    this.checkoutInitPoint = "";
  }

  #selectCheckoutAmount(value) {
    this.checkoutAmount = String(value);
    this.#resetCheckoutSummary();
  }

  #currentPageUrl() {
    if (typeof window === "undefined" || !window.location) return "";
    const { origin, pathname, search } = window.location;
    return `${origin}${pathname}${search}`;
  }

  #prepareLookupCandidates(token) {
    const values = [];
    if (token?.id && typeof token.id === "string") {
      values.push(token.id.trim());
    }
    if (token?.value && typeof token.value === "string") {
      values.push(token.value.trim());
    }
    return Array.from(new Set(values.filter((candidate) => candidate)));
  }

  async #fetchBackendToken(tokenId) {
    const lookup = (tokenId || "").trim();
    if (!lookup || this._syncingTokens.has(lookup)) {
      return null;
    }
    if (!this.#canSyncBackend()) {
      return null;
    }
    this._syncingTokens.add(lookup);
    try {
      const response = await fetch(`${this.#apiBaseUrl}/tokens/${encodeURIComponent(lookup)}`, {
        headers: { Accept: "application/json" }
      });
      if (response.ok) {
        return await response.json();
      }
      if (response.status === 404) {
        return null;
      }
      console.warn(`[near-credits] Failed to sync token ${lookup}: ${response.status}`);
      return null;
    } catch (error) {
      console.warn(`[near-credits] Error syncing token ${lookup}`, error);
      return null;
    } finally {
      this._syncingTokens.delete(lookup);
    }
  }

  async #refreshTokenFromBackend(token) {
    if (!token || !this.#canSyncBackend()) {
      return;
    }
    const candidates = this.#prepareLookupCandidates(token);
    if (!candidates.length) {
      return;
    }
    for (const candidate of candidates) {
      const data = await this.#fetchBackendToken(candidate);
      if (data?.tokenId) {
        const isCurrentlyActive =
          this.activeTokenId === token.id || this.activeTokenId === token.value || this.activeTokenId === data.tokenId;
        const overrideLabel =
          typeof token.label === "string" && token.label.trim() ? token.label.trim() : undefined;
        this._upsertBackendToken(data, {
          labelOverride: overrideLabel,
          setActive: isCurrentlyActive,
          skipMessage: true
        });
        return;
      }
    }
  }

  #refreshAllTokensFromBackend() {
    if (!this.tokens?.length || !this.#canSyncBackend()) {
      return;
    }
    const snapshot = [...this.tokens];
    snapshot.forEach((token) => {
      this.#refreshTokenFromBackend(token);
    });
  }

  syncFromBackend() {
    this.#refreshAllTokensFromBackend();
  }

  #handleCheckoutReturn() {
    this.#resumePendingCheckoutRefresh();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#loadFromStorage();
    this.#attachNearApiListener();
    this.#syncTokensFromNearApi();
    this.#handleCheckoutReturn();
    this.innerHTML = `<div 
      style='position:absolute;top:0;bottom:0;left:0;right:0;
      overflow-y:auto;background-color:white;
      display:none;z-index:2000' id='checkout-container'></div>`;
  }

  disconnectedCallback() {
    this.#clearAllPendingRefreshTimers();
    this.#detachNearApiListener();
    super.disconnectedCallback();
  }

  updated(changedProps) {
    if (changedProps.has("lang")) {
      const resolved = this.#resolveLanguage(this.lang) ?? this.#detectLanguage();
      if (resolved !== this.lang) {
        this.lang = resolved;
        return;
      }
      if (this.message) {
        this.#setMessage();
      }
    }
    if (changedProps.has("apiBase") && this.tokens.length) {
      this._syncingTokens.clear();
      this.#refreshAllTokensFromBackend();
    }
    if (changedProps.has("mpPublicKey")) {
      this.#resetCheckoutSummary();
    }
    if (
      (changedProps.has("activeTokenId") || changedProps.has("tokens") || changedProps.has("showModal")) &&
      this.showModal
    ) {
      this.updateComplete.then(() => this.#syncTokenSelect());
    }
    if (super.updated) {
      super.updated(changedProps);
    }
  }

  get storageKey() {
    return this.getAttribute("storage-key") || STORAGE_KEY;
  }

  get activeToken() {
    return this.tokens.find((token) => token.id === this.activeTokenId) || this.tokens[0] || null;
  }

  getActiveToken() {
    const token = this.activeToken;
    return token ? clone(token) : null;
  }

  get isCheckoutActive() {
    return Boolean(this.checkoutTokenId);
  }

  get displayTheme() {
    if (this.theme !== DEFAULT_THEME) return this.theme;
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  }

  #loadFromStorage() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        this.tokens = [];
        this.activeTokenId = "";
        return;
      }
      const parsed = JSON.parse(raw);
      const sanitized = sanitizeState(parsed, this.#tokenDefaults);
      const mutated =
        Array.isArray(parsed?.tokens) &&
        parsed.tokens.length !== sanitized.tokens.length;
      if (mutated) {
        console.warn(this.t("messages.tokensSanitized"));
      }
      this.tokens = sanitized.tokens;
      this.activeTokenId = sanitized.activeTokenId;
      this.#refreshAllTokensFromBackend();
    } catch (error) {
      console.error("Failed loading credits from storage", error);
      this.tokens = [];
      this.activeTokenId = "";
    }
  }

  #persist() {
    const payload = {
      version: 1,
      tokens: clone(this.tokens),
      activeTokenId: this.activeTokenId || (this.tokens[0]?.id ?? "")
    };
    localStorage.setItem(this.storageKey, JSON.stringify(payload));
    this.#syncTokensToNearApi();
    this.dispatchEvent(
      new CustomEvent("tokens-change", {
        detail: { tokens: clone(this.tokens), activeToken: this.activeToken },
        bubbles: true,
        composed: true
      })
    );
  }

  #setMessage(text = "", type = "info") {
    this.message = text;
    this.messageType = type;
    if (text) {
      clearTimeout(this.messageTimer);
      this.messageTimer = setTimeout(() => {
        this.message = "";
        this.messageType = "info";
        this.messageTimer = null;
      }, 6000);
    }
  }

  #setBadgeMessage(text = "", type = "info", durationMs = 0) {
    const oldMessage = this.badgeMessage;
    const oldType = this.badgeMessageType;
    this.badgeMessage = text;
    this.badgeMessageType = type;
    if (this.badgeMessageTimer) {
      clearTimeout(this.badgeMessageTimer);
      this.badgeMessageTimer = null;
    }
    if (text && durationMs > 0) {
      this.badgeMessageTimer = setTimeout(() => {
        this.badgeMessage = "";
        this.badgeMessageType = "info";
        this.badgeMessageTimer = null;
      }, durationMs);
    }
    this.requestUpdate("badgeMessage", oldMessage);
    this.requestUpdate("badgeMessageType", oldType);
  }

  #nearApi() {
    const api = typeof window !== "undefined" ? window.nearapi : null;
    if (!api || typeof api.getAttribute !== "function" || typeof api.setAttribute !== "function") {
      return null;
    }
    return api;
  }

  #attachNearApiListener() {
    const api = this.#nearApi();
    const canAttach = api && this._nearApiListener;
    if (!canAttach) return;
    if (this._nearApiTarget === api) return;
    this.#detachNearApiListener();
    api.addEventListener("user-ready", this._nearApiListener);
    this._nearApiTarget = api;
    if (typeof window !== "undefined" && this._windowNearApiListener) {
      window.addEventListener("user-ready", this._windowNearApiListener);
      this._nearApiWindowBound = true;
    }
  }

  #detachNearApiListener() {
    if (this._nearApiTarget && this._nearApiListener) {
      this._nearApiTarget.removeEventListener("user-ready", this._nearApiListener);
    }
    if (this._nearApiWindowBound && this._windowNearApiListener && typeof window !== "undefined") {
      window.removeEventListener("user-ready", this._windowNearApiListener);
    }
    this._nearApiTarget = null;
    this._nearApiWindowBound = false;
  }

  #nearApiTokenPayload() {
    const unique = new Map();
    this.tokens.forEach((token) => {
      if (!token?.id) return;
      if (unique.has(token.id)) return;
      unique.set(token.id, {
        id: token.id,
        value: token.value || token.id,
        label: token.label || "",
        status: token.status || "active",
        currency: token.currency || this.currency,
        email: token.email || ""
      });
    });
    return Array.from(unique.values());
  }

  #syncTokensToNearApi() {
    const api = this.#nearApi();
    if (!api) return;
    try {
      const payload = JSON.stringify(this.#nearApiTokenPayload());
      const response = api.setAttribute("credits", payload);
      if (response && typeof response.catch === "function") {
        response.catch((error) => {
          console.warn("[near-credits] failed syncing credits to nearapi", error);
        });
      }
    } catch (error) {
      console.warn("[near-credits] failed syncing credits to nearapi", error);
    }
  }

  #syncTokensFromNearApi() {
    const api = this.#nearApi();
    if (!api) return;
    this.#attachNearApiListener();
    let raw = null;
    try {
      raw = api.getAttribute("credits");
    } catch (error) {
      console.warn("[near-credits] failed reading credits from nearapi", error);
      return;
    }
    if (!raw || typeof raw !== "string") return;
    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      console.warn("[near-credits] invalid nearapi credits payload", error);
      return;
    }
    if (!Array.isArray(parsed) || !parsed.length) return;
    const existing = Array.isArray(this.tokens) ? [...this.tokens] : [];
    const indexById = new Map(existing.map((token, index) => [token.id, index]));
    let changed = false;
    parsed.forEach((entry) => {
      if (!entry || typeof entry !== "object") return;
      const rawId = typeof entry.id === "string" && entry.id.trim() ? entry.id.trim() : "";
      const rawValue = typeof entry.value === "string" && entry.value.trim() ? entry.value.trim() : "";
      const tokenId = rawId || rawValue;
      if (!tokenId) return;
      const tokenValue = rawValue || tokenId;
      const override = {
        id: tokenId,
        value: tokenValue,
        label: typeof entry.label === "string" ? entry.label.trim() : "",
        status: entry.status === "paused" ? "paused" : "active",
        currency: entry.currency || this.currency,
        email: typeof entry.email === "string" ? entry.email.trim() : null
      };
      const incoming = sanitizeToken(override, this.#tokenDefaults);
      if (!incoming) return;
      const index = indexById.get(tokenId);
      if (index === undefined) {
        const fresh = { ...incoming, balance: 0, history: [] };
        existing.push(fresh);
        indexById.set(tokenId, existing.length - 1);
        changed = true;
        return;
      }
      const current = existing[index];
      let updated = current;
      if (!current?.label && incoming.label) {
        updated = { ...updated, label: incoming.label };
      }
      if (!current?.value && incoming.value) {
        updated = { ...updated, value: incoming.value };
      }
      if (!current?.currency && incoming.currency) {
        updated = { ...updated, currency: incoming.currency };
      }
      if (!current?.email && incoming.email) {
        updated = { ...updated, email: incoming.email };
      }
      if (updated !== current) {
        existing[index] = sanitizeToken(updated, this.#tokenDefaults);
        changed = true;
      }
    });
    if (changed) {
      this.tokens = existing;
      this.#persist();
      this.#refreshAllTokensFromBackend();
    }
  }

  addToken(tokenInput = {}, options = {}) {
    const { skipBackendSync = false, setActive = null } = options;
    const prepared = {
      ...tokenInput,
      label: this.#ensureUniqueLabel(tokenInput?.label, { excludeId: tokenInput?.id })
    };
    const token = createToken(prepared, this.#tokenDefaults);
    const existingIndex = this.tokens.findIndex((t) => t.id === token.id);
    if (existingIndex >= 0) {
      const updated = [...this.tokens];
      updated[existingIndex] = token;
      this.tokens = updated;
    } else {
      this.tokens = [...this.tokens, token];
    }
    if (setActive === true) {
      this.activeTokenId = token.id;
    } else if (setActive === false) {
      if (!this.activeTokenId || !this.tokens.some((t) => t.id === this.activeTokenId)) {
        this.activeTokenId = token.id;
      }
    } else if (token.status === "active") {
      this.activeTokenId = token.id;
    } else if (!this.activeTokenId) {
      this.activeTokenId = token.id;
    }
    this.#persist();
    if (!skipBackendSync) {
      this.#refreshTokenFromBackend(token);
    }
    return token;
  }

  removeToken(tokenId) {
    const token = this.tokens.find((t) => t.id === tokenId);
    if (!token) return false;
    if (Number(token.balance) > 0) {
      this.#setMessage(this.t("messages.invalidDelete"), "error");
      return false;
    }
    this.tokens = this.tokens.filter((t) => t.id !== tokenId);
    if (this.activeTokenId === tokenId) {
      this.activeTokenId = this.tokens[0]?.id ?? "";
    }
    this.#persist();
    return true;
  }

  setActiveToken(tokenId) {
    if (this.activeTokenId === tokenId) return;
    const exists = this.tokens.some((t) => t.id === tokenId);
    if (!exists) {
      this.#setMessage(this.t("messages.cannotActivate"), "error");
      return;
    }
    this.activeTokenId = tokenId;
    this.#persist();
  }

  handleTokenSelectChange(event) {
    const select = event?.currentTarget || event?.target;
    if (!select) return;
    const value = select.value;
    const fallback = this.activeTokenId || (this.tokens[0]?.id ?? "");
    if (value === "__create__") {
      select.value = fallback;
      this.openAddToken();
      return;
    }
    if (!value) {
      select.value = fallback;
      return;
    }
    if (value !== this.activeTokenId) {
      this.setActiveToken(value);
    }
  }

  listTokens() {
    return clone(this.tokens);
  }

  pauseToken(tokenId, paused = true) {
    this.tokens = this.tokens.map((token) =>
      token.id === tokenId
        ? {
            ...token,
            status: paused ? "paused" : "active",
            updatedAt: new Date().toISOString()
          }
        : token
    );
    if (paused && this.activeTokenId === tokenId) {
      this.activeTokenId = this.tokens.find((t) => t.status === "active")?.id ?? "";
    }
    this.#persist();
  }

  updateToken(tokenId, changes = {}) {
    this.tokens = this.tokens.map((token) => {
      if (token.id !== tokenId) return token;
      const merged = {
        ...token,
        ...changes,
        id: token.id,
        balance: Number.isFinite(Number(changes.balance)) ? Number(changes.balance) : token.balance,
        updatedAt: new Date().toISOString()
      };
      merged.label = this.#ensureUniqueLabel(merged.label, { excludeId: token.id });
      return sanitizeToken(merged, this.#tokenDefaults);
    });
    this.#persist();
  }

  adjustBalance(tokenId, delta) {
    this.tokens = this.tokens.map((token) =>
      token.id === tokenId
        ? {
            ...token,
            balance: Math.max(0, Number(token.balance) + Number(delta || 0)),
            updatedAt: new Date().toISOString()
          }
        : token
    );
    this.#persist();
  }

  backupTokens() {
    const payload = {
      exportedAt: new Date().toISOString(),
      tokens: clone(this.tokens),
      activeTokenId: this.activeTokenId
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `near-credits-backup-${Date.now()}.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    this.#setMessage(this.t("messages.backupSuccess"), "success");
  }

  async restoreTokensFromFile(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const sanitized = sanitizeState(parsed, this.#tokenDefaults);
      const existingTokens = Array.isArray(this.tokens) ? [...this.tokens] : [];
      const mergedTokens = [...existingTokens];
      const indexById = new Map(existingTokens.map((token, index) => [token.id, index]));
      sanitized.tokens.forEach((incomingToken) => {
        const index = indexById.get(incomingToken.id);
        if (index === undefined) {
          indexById.set(incomingToken.id, mergedTokens.length);
          mergedTokens.push(incomingToken);
          return;
        }
        const existingToken = mergedTokens[index];
        const incomingUpdated = Date.parse(incomingToken.updatedAt || "");
        const existingUpdated = Date.parse(existingToken?.updatedAt || "");
        const incomingIsNewer =
          !Number.isNaN(incomingUpdated) &&
          (Number.isNaN(existingUpdated) || incomingUpdated > existingUpdated);
        if (incomingIsNewer) {
          mergedTokens[index] = incomingToken;
        }
      });
      const existingActiveId = this.activeTokenId;
      let nextActiveId = "";
      if (existingActiveId && mergedTokens.some((token) => token.id === existingActiveId)) {
        nextActiveId = existingActiveId;
      } else if (
        sanitized.activeTokenId &&
        mergedTokens.some((token) => token.id === sanitized.activeTokenId)
      ) {
        nextActiveId = sanitized.activeTokenId;
      } else {
        nextActiveId = mergedTokens[0]?.id || "";
      }
      this.tokens = mergedTokens;
      this.activeTokenId = nextActiveId;
      this.#persist();
      this.#refreshAllTokensFromBackend();
      this.#setMessage(this.t("messages.restoreSuccess"), "success");
    } catch (error) {
      console.error("Failed to restore tokens", error);
      this.#setMessage(this.t("messages.restoreError"), "error");
    } finally {
      event.target.value = "";
    }
  }

  handleCopy(token) {
    if (!navigator.clipboard) {
      this.#setMessage(this.t("messages.clipboardUnavailable"), "error");
      return;
    }
    navigator.clipboard
      .writeText(token.value)
      .then(() => this.#setMessage(this.t("messages.copySuccess"), "success"))
      .catch((error) => {
        console.error("Copy failed", error);
        this.#setMessage(this.t("messages.copyError"), "error");
      });
  }

  async handleShare(token) {
    if (!navigator.share) {
      this.#setMessage(this.t("messages.shareUnavailable"), "error");
      return;
    }
    try {
      await navigator.share({
        title: this.t("share.title"),
        text: this.t("share.text", token.label, token.value)
      });
      this.#setMessage(this.t("messages.shareSuccess"), "success");
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Share failed", error);
        this.#setMessage(this.t("messages.shareError"), "error");
      }
    }
  }

  showTransfer(tokenId) {
    this.transferFromId = tokenId;
    const firstTarget = this.tokens.find((token) => token.id !== tokenId)?.id ?? "";
    this.transferTargetId = firstTarget;
    this.transferAmount = "";
  }

  cancelTransfer() {
    this.transferFromId = "";
    this.transferTargetId = "";
    this.transferAmount = "";
  }

  openAddToken(mode = "create") {
    if (!this.showModal) {
      this.openModal();
    }
    const defaultLabel = this.#generateDefaultLabel();
    this.addTokenMode = mode;
    this.draftLabel = defaultLabel;
    this.createEmail = "";
    this.importTokenId = "";
    this.importLabel = defaultLabel;
    this.addTokenVisible = true;
    this.updateComplete.then(() => {
      const selector = mode === "create" ? "#nc-create-token-label" : "#nc-import-token-id";
      const input = this.renderRoot?.querySelector(selector);
      input?.focus();
    });
  }

  cancelAddToken() {
    this.addTokenVisible = false;
    this.addTokenMode = "create";
    this.draftLabel = "";
    this.createEmail = "";
    this.importTokenId = "";
    this.importLabel = "";
    this.busy = false;
  }

  executeTransfer(event) {
    event?.preventDefault();
    const amount = Number(this.transferAmount);
    const from = this.tokens.find((token) => token.id === this.transferFromId);
    const target = this.tokens.find((token) => token.id === this.transferTargetId);
    if (!from || !target) {
      this.#setMessage(this.t("messages.transferSelect"), "error");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      this.#setMessage(this.t("messages.transferPositive"), "error");
      return;
    }
    if (amount > Number(from.balance)) {
      this.#setMessage(this.t("messages.transferBalance"), "error");
      return;
    }
    this.adjustBalance(from.id, -amount);
    this.adjustBalance(target.id, amount);
    this.#setMessage(this.t("messages.transferSuccess"), "success");
    this.cancelTransfer();
  }

  openModal() {
    this.showModal = true;
    this.updateComplete.then(() => {
      const dialog = this.renderRoot?.querySelector("dialog");
      dialog?.showModal();
      this.#syncTokenSelect();
    });
  }

  closeModal() {
    const dialog = this.renderRoot?.querySelector("dialog");
    dialog?.close();
  }

  #handleDialogClose() {
    this.showModal = false;
    if (this._suspendDialogCleanup) {
      this._suspendDialogCleanup = false;
      return;
    }
    this.cancelTransfer();
    this.cancelAddToken();
    this.cancelCheckout();
  }

  beginCheckoutForActive() {
    const token = this.activeToken;
    if (!token) {
      this.#setMessage(this.t("messages.checkoutSelect"), "error");
      return;
    }
    this.beginCheckout(token.id);
  }

  beginCheckout(tokenId) {
    this.checkoutTokenId = tokenId;
    this.checkoutAmount = "5";
    this.#resetCheckoutSummary();
  }

  cancelCheckout({ clearPending = true } = {}) {
    this.checkoutTokenId = "";
    this.checkoutAmount = "";
    this.busy = false;
    this.#resetCheckoutSummary();
    this.#checkoutContainerVisible(false);
    if (clearPending) {
      this.#consumePendingCheckout();
      this.#setBadgeMessage("", "info", 0);
    }
  }

  #checkoutCurrencyId(token) {
    const source = (token?.currency || this.currency || "USD").toString().toUpperCase();
    if (source === "CREDITS") return "USD";
    return source;
  }

  #checkoutContainerVisible(visible) {
    if (visible) {
      this.querySelector("#checkout-container").style.display = "block" ;
    } else {
      this.querySelector("#checkout-container").style.display = "none";
    }
  }

  #handlePaymentStart(event) {
    const token = this.tokens.find((t) => t.id === this.checkoutTokenId);
    const amountValue = Number(this.checkoutAmount);
    console.log("Starting payment for token", token, "amount", amountValue);
    this.#checkoutContainerVisible(true);
    if (token) {
      this.#recordPendingCheckout({ tokenId: token.id });
      this.#setBadgeMessage(this.t("messages.checkoutPending"), "info", 0);
    }
    if (!token) {
      event?.preventDefault();
      this.busy = false;
      this.#setMessage(this.t("messages.checkoutSelect"), "error");
      return;
    }
    if (!Number.isFinite(amountValue) || amountValue < 5) {
      event?.preventDefault();
      this.busy = false;
      const minMessage = this.#locale === "es" ? "El monto mínimo es 5 USD." : "Minimum amount is 5 USD.";
      this.#setMessage(minMessage, "error");
      return;
    }
    this.busy = true;
    this.#resetCheckoutSummary();
  }

  #handlePaymentReady() {
    this.busy = false;
    this.#setMessage(this.t("messages.checkoutOpened"), "info");
  }

  #handlePaymentSubmit() {
    this.busy = true;
  }

  #handlePaymentSuccess(event) {
    const result = event?.detail?.result ?? event?.detail ?? {};
    const payment = result?.body ?? result;
    const status = typeof payment?.status === "string" ? payment.status.toLowerCase() : "";
    const paymentId = payment?.id;
    this.#checkoutContainerVisible(false);
    let messageKey = "messages.checkoutApproved";
    let type = "success";
    if (status === "pending" || status === "in_process") {
      messageKey = "messages.checkoutPending";
      type = "info";
    } else if (status && status !== "approved") {
      messageKey = "messages.checkoutFailure";
      type = "error";
    }
    this.busy = false;
    this.#setMessage(this.t(messageKey), type);
    const token = this.tokens.find((t) => t.id === this.checkoutTokenId);
    if (token) {
      this.#recordPendingCheckout({ tokenId: token.id, paymentId });
      this.#refreshTokenFromBackend(token);
      if (paymentId) {
        this.#schedulePaymentStatusWatch({ paymentId, tokenId: token.id });
      } else {
        this.#schedulePostCheckoutRefresh(token.id);
      }
    } else {
      this.#refreshAllTokensFromBackend();
    }
    this.cancelCheckout({ clearPending: false });
    this.closeModal();
    this.#setBadgeMessage(this.t("messages.checkoutPending"), "info", 0);
  }

  #handlePaymentError(event) {
    const error = event?.detail?.error;
    if (error) {
      console.error("MercadoPago payment error", error);
    }
    this.#checkoutContainerVisible(false);
    this.busy = false;
    this.#setMessage(this.t("messages.checkoutError"), "error");
    this.#setBadgeMessage(this.t("messages.checkoutError"), "error", 6000);
    this.#consumePendingCheckout();
    this.#refreshAllTokensFromBackend();
  }

  renderTokenBadge() {
    const token = this.activeToken;
    const initials = this.#initialsForToken(token);
    const emptyTitle = this.t("badge.emptyTitle");
    const emptySubtitle = this.t("badge.emptySubtitle");
    const balanceValue = token
      ? formatNumber(token.balance, token.currency || this.currency, this.#locale)
      : "";
    const statusKey = token?.status === "paused" ? "paused" : "active";
    const statusLabel = token ? this.t(`statuses.${statusKey}`) : "";
    const subtitle = token ? this.t("badge.subtitle", balanceValue, statusLabel) : emptySubtitle;
    const badgeStatus = this.badgeMessage || subtitle;
    return html`
      <button class="nc-trigger" @click=${() => this.openModal()} aria-haspopup="dialog">
        <div class="trigger-left">
          <span class="avatar">${initials}</span>
          <span class="summary">
            <strong>${token ? balanceValue : emptyTitle}</strong>
            <small>${badgeStatus}</small>
          </span>
        </div>
        ${this.badgeMessage ? html`<span class="badge-pill ${this.badgeMessageType}">${this.badgeMessage}</span>` : ""}
      </button>
    `;
  }

  renderTransferForm() {
    if (!this.transferFromId) return null;
    const availableTargets = this.tokens.filter((token) => token.id !== this.transferFromId);
    return html`
      <section class="section">
        <h3>${this.t("headings.transfer")}</h3>
        <form @submit=${(event) => this.executeTransfer(event)} class="section">
          <label class="field">
            ${this.t("fields.fromToken")}
            <input
              type="text"
              .value=${this.tokens.find((token) => token.id === this.transferFromId)?.label ?? ""}
              disabled
            />
          </label>
          <label class="field">
            ${this.t("fields.toToken")}
            <select .value=${this.transferTargetId} @change=${(event) => (this.transferTargetId = event.target.value)} required>
              <option value="" disabled>${this.t("fields.selectToken")}</option>
              ${availableTargets.map(
                (token) => html`<option value=${token.id}>${token.label}</option>`
              )}
            </select>
          </label>
          <label class="field">
            ${this.t("fields.transferAmount")}
            <input
              type="number"
              min="0"
              step="0.01"
              inputmode="decimal"
              .value=${this.transferAmount}
              @input=${(event) => (this.transferAmount = event.target.value)}
              required
            />
          </label>
          <div class="token-actions">
            <button class="nc-btn primary" type="submit">${this.t("actions.transfer")}</button>
            <button class="nc-btn" type="button" @click=${() => this.cancelTransfer()}>
              ${this.t("actions.cancel")}
            </button>
          </div>
        </form>
      </section>
    `;
  }

  renderAddTokenOverlay() {
    if (!this.addTokenVisible) return null;
    const isCreate = this.addTokenMode === "create";
    return html`
      <div
        class="add-token-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="nc-add-token-title"
        @click=${(event) => {
          if (event.target === event.currentTarget) this.cancelAddToken();
        }}
      >
        <div class="add-token-card">
          <header class="add-token-head">
            <h3 id="nc-add-token-title">
              ${this.t(isCreate ? "overlay.createTitle" : "overlay.importTitle")}
            </h3>
            ${this.renderIconButton("x", this.t("actions.closeOverlay"), () => this.cancelAddToken())}
          </header>
          <div class="mode-switch">
            <button
              type="button"
              class=${this.addTokenMode === "create" ? "active" : ""}
              @click=${() => this.setAddTokenMode("create")}
            >
              ${this.t("overlay.mode.create")}
            </button>
            <button
              type="button"
              class=${this.addTokenMode === "import" ? "active" : ""}
              @click=${() => this.setAddTokenMode("import")}
            >
              ${this.t("overlay.mode.import")}
            </button>
          </div>
          <p class="token-meta" style="margin: 0;">
            ${this.t(isCreate ? "overlay.createDescription" : "overlay.importDescription")}
          </p>
          ${isCreate ? this.renderCreateTokenForm() : this.renderImportTokenForm()}
        </div>
      </div>
    `;
  }

  setAddTokenMode(mode) {
    if (this.addTokenMode === mode) return;
    this.addTokenMode = mode;
    this.busy = false;
    if (mode === "create" && !this.draftLabel) {
      this.draftLabel = this.#generateDefaultLabel();
    }
    if (mode === "import" && !this.importLabel) {
      this.importLabel = this.#generateDefaultLabel();
    }
    this.updateComplete.then(() => {
      const selector = mode === "create" ? "#nc-create-token-label" : "#nc-import-token-id";
      const input = this.renderRoot?.querySelector(selector);
      input?.focus();
    });
  }

  renderCreateTokenForm() {
    return html`
      <form class="add-token-form" @submit=${(event) => this.submitCreateToken(event)}>
        <label class="field">
          ${this.t("fields.label")}
          <input
            id="nc-create-token-label"
            .value=${this.draftLabel}
            placeholder=${this.t("defaults.tokenLabel")}
            @input=${(event) => (this.draftLabel = event.target.value)}
          />
        </label>
        <label class="field">
          ${this.t("overlay.emailLabel")}
          <input
            type="email"
            .value=${this.createEmail}
            @input=${(event) => (this.createEmail = event.target.value)}
            placeholder="user@example.com"
          />
        </label>
        <div class="add-token-actions">
          <button class="nc-btn" type="button" @click=${() => this.cancelAddToken()}>
            ${this.t("actions.cancel")}
          </button>
          <button class="nc-btn primary" type="submit" ?disabled=${this.busy}>
            ${this.t("actions.createToken")}
          </button>
        </div>
      </form>
    `;
  }

  renderImportTokenForm() {
    return html`
      <form class="add-token-form" @submit=${(event) => this.submitImportToken(event)}>
        <label class="field">
          ${this.t("overlay.tokenPlaceholder")}
          <input
            id="nc-import-token-id"
            .value=${this.importTokenId}
            required
            @input=${(event) => (this.importTokenId = event.target.value)}
            autocomplete="off"
          />
        </label>
        <label class="field">
          ${this.t("fields.label")}
          <input
            .value=${this.importLabel}
            @input=${(event) => (this.importLabel = event.target.value)}
            placeholder=${this.t("defaults.tokenLabel")}
          />
        </label>
        <div class="add-token-actions">
          <button class="nc-btn" type="button" @click=${() => this.cancelAddToken()}>
            ${this.t("actions.cancel")}
          </button>
          <button class="nc-btn primary" type="submit" ?disabled=${this.busy}>
            ${this.t("actions.importToken")}
          </button>
        </div>
      </form>
    `;
  }

  async submitCreateToken(event) {
    event?.preventDefault();
    if (this.busy) return;
    this.busy = true;
    try {
      const uniqueLabel = this.#ensureUniqueLabel(this.draftLabel);
      this.draftLabel = uniqueLabel;
      const response = await fetch(`${this.apiBase}/tokens`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: uniqueLabel,
          email: this.createEmail?.trim() || undefined
        })
      });
      if (!response.ok) {
        throw new Error(`create-token failed (${response.status})`);
      }
      const data = await response.json();
      this._upsertBackendToken(data, {
        labelOverride: this.draftLabel?.trim(),
        messageKey: "messages.createTokenSuccess"
      });
      this.cancelAddToken();
    } catch (error) {
      console.error("create token", error);
      this.#setMessage(this.t("messages.createTokenError"), "error");
    } finally {
      this.busy = false;
    }
  }

  async submitImportToken(event) {
    event?.preventDefault();
    const tokenId = this.importTokenId?.trim();
    if (!tokenId) {
      this.#setMessage(this.t("messages.importTokenRequired"), "error");
      return;
    }
    const uniqueLabel = this.#ensureUniqueLabel(this.importLabel);
    this.importLabel = uniqueLabel;
    this.busy = true;
    try {
      const response = await fetch(`${this.apiBase}/tokens/${encodeURIComponent(tokenId)}`);
      if (response.status === 404) {
        this.#setMessage(this.t("messages.importTokenNotFound"), "error");
        return;
      }
      if (!response.ok) {
        throw new Error(`import-token failed (${response.status})`);
      }
      const data = await response.json();
      this._upsertBackendToken(data, {
        labelOverride: uniqueLabel,
        messageKey: "messages.importTokenSuccess"
      });
      this.cancelAddToken();
    } catch (error) {
      console.error("import token", error);
      this.#setMessage(this.t("messages.importTokenError"), "error");
    } finally {
      this.busy = false;
    }
  }

  _upsertBackendToken(data, { labelOverride, messageKey, setActive = true, skipMessage = false } = {}) {
    if (!data?.tokenId) {
      throw new Error("Invalid token payload from backend");
    }
    const tokenPayload = {
      id: data.tokenId,
      value: data.tokenId,
      label: labelOverride || data.label || undefined,
      balance: Number.isFinite(Number(data.balance)) ? Number(data.balance) : 0,
      currency: data.currency || this.currency,
      status: data.status || "active",
      email: data.email,
      createdAt: data.createdAt || new Date().toISOString(),
      updatedAt: data.updatedAt || new Date().toISOString(),
      history: Array.isArray(data.history) ? data.history : []
    };

    const token = this.addToken(tokenPayload, { skipBackendSync: true, setActive });
    if (messageKey && !skipMessage) {
      this.#setMessage(this.t(messageKey, token.label || token.id), "success");
    }
    return token;
  }

  renderCheckoutForm() {
    if (!this.checkoutTokenId) return null;
    const token = this.tokens.find((t) => t.id === this.checkoutTokenId);
    if (!token) return null;
    const presets = [5, 10, 20, 50];
    const selectedAmount = Number(this.checkoutAmount) || 0;
    const displayCurrency = this.#checkoutCurrencyId(token);
    const minNote = this.#locale === "es" ? "Monto mínimo 5 USD." : "Minimum amount 5 USD.";
    const amountValue = Number(this.checkoutAmount);
    const amountValid = Number.isFinite(amountValue) && amountValue >= 5;
    const items = amountValid
      ? [
          {
            id: token.id,
            title: token.label ? `${window.location.host} ${token.label} credits ` : `${window.location.host} credits`,
            quantity: 1,
            unit_price: amountValue,
            currency_id: displayCurrency
          }
        ]
      : [];
    const metadata = {
      tokenId: token.id,
      tokenValue: token.value
    };
    const disableCheckout = this.busy || !amountValid || !this.mpPublicKey;
    return html`
      <section class="section">
        <h3>${this.t("headings.checkout")}</h3>
        <form class="checkout-form" @submit=${(event) => event.preventDefault()}>
          <p class="token-meta">
            ${this.t("checkout.forPrefix")} <strong>${token?.label ?? ""}</strong>
          </p>
          <div class="amount-options">
            ${presets.map((value) => {
              const label = `U$S ${value}`;
              const isActive = selectedAmount === value;
              return html`<button
                type="button"
                class=${isActive ? "active" : ""}
                @click=${() => this.#selectCheckoutAmount(value)}
              >
                ${label}
              </button>`;
            })}
          </div>
          <label class="field">
            ${this.t("fields.checkoutAmount", displayCurrency)}
            <input
              type="number"
              min="5"
              step="1"
              inputmode="decimal"
              .value=${this.checkoutAmount}
              placeholder="5"
              @input=${(event) => {
                this.checkoutAmount = event.target.value;
                this.#resetCheckoutSummary();
              }}
              required
            />
            <small class="field-hint">${minNote}</small>
          </label>
          ${this.active? html`<slot></slot>`: ``}
          <div class="token-actions">
            <near-mercado-pago
              .lang=${this.lang}
              .publicKey=${this.mpPublicKey || ""}
              .apiBase=${this.apiBase}
              .items=${items}
              .metadata=${metadata}
              ?disabled=${disableCheckout}
              @payment-start=${(event) => this.#handlePaymentStart(event)}
              @payment-ready=${() => this.#handlePaymentReady()}
              @payment-submit=${() => this.#handlePaymentSubmit()}
              @payment-success=${(event) => this.#handlePaymentSuccess(event)}
              @payment-error=${(event) => this.#handlePaymentError(event)}
            ></near-mercado-pago>
            <button class="nc-btn" type="button" @click=${() => this.cancelCheckout()}>
              ${this.t("actions.cancel")}
            </button>
          </div>
        </form>
      </section>
    `;
  }

  renderIconButton(iconKey, label, handler, options = {}) {
    const { variant = "", disabled = false } = options;
    const classes = ["nc-btn"];
    if (variant) classes.push(variant);
    const iconTemplate = ICONS[iconKey] ?? svg``;
    return html`
      <button
        class=${classes.join(" ")}
        type="button"
        title=${label}
        aria-label=${label}
        @click=${handler}
        ?disabled=${disabled}
      >
        ${iconTemplate}
        <span class="sr-only">${label}</span>
      </button>
    `;
  }

  renderTokenList() {
    if (!this.tokens.length) {
      return html`
        <div class="token-card token-empty">
          <p>${this.t("token.empty")}</p>
          <button class="nc-btn primary" type="button" @click=${() => this.openAddToken()}>
            ${this.t("actions.openManualFab")}
          </button>
        </div>
      `;
    }
    const activeToken = this.activeToken;
    if (!activeToken) {
      return null;
    }
    const selectValue = this.activeTokenId || activeToken.id;
    const units = this.#currencyUnits(activeToken);
    const balanceAmount = formatNumber(
      activeToken.balance,
      activeToken.currency || this.currency,
      this.#locale
    );
    const balanceLabel = this.t("token.balanceLabel");
    const balanceText = units ? `${units} ${balanceLabel}` : balanceLabel;
    const statusKey = activeToken.status === "paused" ? "paused" : "active";
    return html`
      <article class="token-card" data-token-id=${activeToken.id}>
        <header class="token-header token-header--select">
          <label class="token-select">
            <span class="token-select-label">${this.t("fields.selectToken")}</span>
            <select
              @change=${(event) => this.handleTokenSelectChange(event)}
              .value=${selectValue}
            >
              ${this.tokens.map((token) => {
                const optionLabel = token.label || this.t("defaults.tokenLabel");
                return html`<option value=${token.id}>${optionLabel}</option>`;
              })}
              <option value="__create__">${this.t("actions.createTokenOption")}</option>
            </select>
          </label>
          <div class="token-status">
            <span class="chip ${statusKey === "paused" ? "paused" : ""}">
              ${this.t(`statuses.${statusKey}`)}
            </span>
            <span class="token-date">${this.t("token.updated", this.#formatDate(activeToken.updatedAt))}</span>
          </div>
        </header>
        <div class="token-details">
          <div class="token-meta-value">${activeToken.value}</div>
          ${activeToken.email
            ? html`<div class="token-email">${activeToken.email}</div>`
            : null}
        </div>
        <div class="token-meta token-summary">
          <div class="token-balance">
            <span class="token-balance-amount">${balanceAmount}</span>
            <span class="token-balance-label">${balanceText}</span>
          </div>
        </div>
        <footer class="token-actions">
          ${activeToken.status !== "paused"
            ? this.renderIconButton(
                "pause",
                this.t("actions.pauseToken", activeToken.label || activeToken.id),
                () => this.pauseToken(activeToken.id, true)
              )
            : this.renderIconButton(
                "play",
                this.t("actions.resumeToken", activeToken.label || activeToken.id),
                () => this.pauseToken(activeToken.id, false)
              )}
          ${this.renderIconButton(
            "transfer",
            this.t("actions.transferFrom", activeToken.label || activeToken.id),
            () => this.showTransfer(activeToken.id)
          )}
          ${this.renderIconButton(
            "copy",
            this.t("actions.copyToken", activeToken.label || activeToken.id),
            () => this.handleCopy(activeToken)
          )}
          ${this.renderIconButton(
            "share",
            this.t("actions.shareToken", activeToken.label || activeToken.id),
            () => this.handleShare(activeToken)
          )}
          ${this.renderIconButton(
            "trash",
            this.t("actions.deleteToken", activeToken.label || activeToken.id),
            () => this.removeToken(activeToken.id),
            { variant: "danger" }
          )}
          <button
          class="nc-cta"
          type="button"
          title=${this.t("actions.checkoutFab")}
          @click=${() => this.beginCheckoutForActive()}
          ?disabled=${this.addTokenVisible || !this.tokens.length || this.isCheckoutActive || this.busy}
          aria-disabled=${this.addTokenVisible || !this.tokens.length || this.isCheckoutActive || this.busy ? "true" : "false"}
        >
          ${ICONS.cart}
          <span>${this.t("actions.checkoutFab")}</span>
          </button>
        </footer>
      </article>
    `;
  }

  render() {
    this.theme = this.displayTheme;
    return html`
      ${this.renderTokenBadge()}
      <input
        class="sr-only"
        type="file"
        accept="application/json"
        @change=${(event) => this.restoreTokensFromFile(event)}
        id="nc-restore-input"
      />
      <dialog @close=${(event) => this.#handleDialogClose(event)}>
        ${this.renderAddTokenOverlay()}
        <slot></slot>
        <header class="modal-head">
          <h2>${this.t("headings.modalTitle")}</h2>
          <div class="token-actions">
            <button class="nc-btn" type="button" @click=${() => this.closeModal()}>
              ${this.t("actions.close")}
            </button>
          </div>
        </header>
        <section class="modal-body ${this.isCheckoutActive ? "checkout-active" : ""}">
          <div class="tokens">${this.renderTokenList()}
          
          
          
          </div>
          <div class="section">
            ${this.message
              ? html`<div class="message ${this.messageType === "error" ? "error" : this.messageType === "success" ? "success" : ""}">
                  ${this.message}
                </div>`
              : null}
            ${this.renderTransferForm()} ${this.renderCheckoutForm()}
          </div>
        </section>
        
        <footer class="modal-footer">
          <small style="color: var(--nc-muted);">${this.t("footer.persisted")}</small>
          <div class="footer-actions">
            <button
              class="nc-btn secondary"
              type="button"
              @click=${() => this.backupTokens()}
              ?disabled=${!this.tokens.length}
            >
              ${this.t("actions.backup")}
            </button>
            <button
              class="nc-btn secondary"
              type="button"
              @click=${() => this.renderRoot.getElementById("nc-restore-input").click()}
            >
              ${this.t("actions.restore")}
            </button>
          </div>
        </footer>
      </dialog>
    `;
  }
}

if (!customElements.get("near-credits")) {
  customElements.define("near-credits", NearCredits);
}

export { NearCredits };
